import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { useT } from "@/i18n";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Plus, Pencil, Trash2, Tag } from "lucide-react";
import { toast } from "sonner";

export const POSITION_COLORS = ["#EAB308", "#3B82F6", "#8B5CF6", "#10B981", "#EF4444", "#F97316", "#06B6D4", "#EC4899", "#64748B", "#14B8A6"];

export default function PositionsManager({ onChange }) {
  const { t } = useT();
  const [positions, setPositions] = useState([]);
  const [form, setForm] = useState(null);

  const load = useCallback(() => {
    api.get("/positions").then((r) => setPositions(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.name?.trim()) return toast.error(t("positionName"));
    try {
      if (form.id) await api.put(`/positions/${form.id}`, form);
      else await api.post("/positions", form);
      setForm(null); load(); onChange && onChange();
      toast.success(t("save"));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (p) => {
    if (!window.confirm(`${t("delete")} "${p.name}"?`)) return;
    try { await api.delete(`/positions/${p.id}`); load(); onChange && onChange(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-3" data-testid="positions-manager">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5"><Tag className="w-3.5 h-3.5" />{t("positions")}</p>
        {!form && <Button size="sm" variant="outline" onClick={() => setForm({ name: "", color: POSITION_COLORS[0], description: "", active: true })} data-testid="add-position"><Plus className="w-4 h-4 mr-1" />{t("addPosition")}</Button>}
      </div>

      {form && (
        <div className="rounded-xl border border-border p-4 space-y-3 bg-muted/30" data-testid="position-form">
          <div className="space-y-1.5"><Label className="text-xs">{t("positionName")}</Label><Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="position-name" placeholder="Day Support Worker" /></div>
          <div className="space-y-1.5"><Label className="text-xs">{t("positionColor")}</Label>
            <div className="flex flex-wrap gap-2">{POSITION_COLORS.map((c) => <button key={c} onClick={() => setForm((f) => ({ ...f, color: c }))} style={{ background: c }} className={`w-7 h-7 rounded-full transition-transform ${form.color === c ? "ring-2 ring-offset-2 ring-foreground scale-110" : ""}`} data-testid={`position-color-${c}`} />)}</div>
          </div>
          <div className="space-y-1.5"><Label className="text-xs">{t("description")}</Label><Textarea rows={2} value={form.description || ""} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} /></div>
          <label className="flex items-center gap-2 text-sm cursor-pointer"><input type="checkbox" className="w-4 h-4" checked={!!form.active} onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))} data-testid="position-active" />{t("active")}</label>
          <div className="flex gap-2 justify-end">
            <Button size="sm" variant="outline" onClick={() => setForm(null)}>{t("cancel")}</Button>
            <Button size="sm" onClick={save} data-testid="save-position">{t("save")}</Button>
          </div>
        </div>
      )}

      <div className="space-y-1.5">
        {positions.length === 0 && !form && <p className="text-sm text-muted-foreground">{t("noPositions")}</p>}
        {positions.map((p) => (
          <div key={p.id} className="flex items-center gap-3 rounded-lg border border-border px-3 py-2" data-testid="position-row">
            <span className="w-3.5 h-3.5 rounded-full shrink-0" style={{ background: p.color }} />
            <div className="flex-1 min-w-0">
              <span className="text-sm font-medium">{p.name}</span>
              {!p.active && <Badge variant="secondary" className="ml-2 text-[9px]">{t("inactive")}</Badge>}
              {p.description && <p className="text-xs text-muted-foreground truncate">{p.description}</p>}
            </div>
            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setForm(p)} data-testid="edit-position"><Pencil className="w-3.5 h-3.5" /></Button>
            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => del(p)} data-testid="delete-position"><Trash2 className="w-3.5 h-3.5 text-destructive" /></Button>
          </div>
        ))}
      </div>
    </div>
  );
}
